import { supabase } from '../lib/supabase';

/**
 * User profile service for managing user settings and profile data
 */
export class UserProfileService {
  
  /**
   * Get current user profile data
   * @returns {Promise<Object>} User profile data
   */
  static async getCurrentUserProfile() {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      console.log('Session check:', { session: !!session, sessionError });
      
      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      console.log('Calling users-me function with token:', session.access_token.substring(0, 20) + '...');

      // Use direct fetch instead of supabase.functions.invoke to avoid POST default
      const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/users-me`;
      
      const response = await fetch(functionUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        }
      });

      console.log('users-me response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('users-me error:', errorText);
        throw new Error(`Failed to fetch user profile: ${response.status}`);
      }

      const data = await response.json();
      console.log('users-me response data:', data);

      return data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      console.error('Error stack:', error.stack);
      throw error;
    }
  }

  /**
   * Update user profile data
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<Object>} Update result
   */
  static async updateUserProfile(profileData) {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      // Use direct fetch for PUT request
      const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/users-me`;
      
      const response = await fetch(functionUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Update profile error:', errorText);
        throw new Error(`Failed to update user profile: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  /**
   * Upload user avatar
   * @param {File} file - Avatar image file
   * @returns {Promise<Object>} Upload result with signed URL
   */
  static async uploadAvatar(file) {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      // Validate file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        throw new Error('File size exceeds 2MB limit');
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Only JPEG and PNG are allowed');
      }

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('avatar', file);

      // Call Edge Function to upload avatar using fetch
      const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/avatar-upload`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Avatar upload error:', errorText);
        throw new Error(`Failed to upload avatar: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      return data;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      throw error;
    }
  }

  /**
   * Get signed URL for user avatar
   * @returns {Promise<string>} Signed URL for avatar
   */
  static async getAvatarUrl() {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      // Use direct fetch for avatar URL
      const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/avatar-url`;
      
      const response = await fetch(functionUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (errorText.includes('No avatar found')) {
          return null; // No avatar uploaded yet
        }
        console.error('Get avatar URL error:', errorText);
        throw new Error(`Failed to get avatar URL: ${response.status}`);
      }

      const data = await response.json();
      return data.signed_url;
    } catch (error) {
      console.error('Error getting avatar URL:', error);
      if (error.message?.includes('No avatar found')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Validate profile data before submission
   * @param {Object} profileData - Profile data to validate
   * @returns {Object} Validation result with errors
   */
  static validateProfileData(profileData) {
    const errors = {};

    // Email validation
    if (profileData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      errors.email = '有効なメールアドレスを入力してください。';
    }

    // Phone validation
    if (profileData.phone && !/^(0[0-9]{9,10}|\+81[0-9]{9,10})$/.test(profileData.phone)) {
      errors.phone = '正しい電話番号形式で入力してください（ハイフンなし）。';
    }

    // Birth date validation
    if (profileData.birth_date) {
      const birthDate = new Date(profileData.birth_date);
      const today = new Date();
      if (birthDate > today) {
        errors.birth_date = '生年月日は未来の日付にできません。';
      }
    }

    // Address length validation
    if (profileData.address && profileData.address.length > 500) {
      errors.address = '住所は500文字以内で入力してください。';
    }

    // Gender validation
    if (profileData.gender && !['male', 'female', 'other', 'prefer_not_to_say'].includes(profileData.gender)) {
      errors.gender = '無効な性別が選択されています。';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  /**
   * Format profile data for API submission
   * @param {Object} formData - Raw form data
   * @returns {Object} Formatted profile data
   */
  static formatProfileData(formData) {
    const profileData = {};

    // Map form fields to API fields
    if (formData.name !== undefined) profileData.name = formData.name;
    if (formData.phone !== undefined) profileData.phone = formData.phone;
    if (formData.email !== undefined) profileData.email = formData.email;
    if (formData.timezone !== undefined) profileData.timezone = formData.timezone;
    if (formData.language !== undefined) profileData.language = formData.language;
    if (formData.occupation !== undefined) profileData.occupation = formData.occupation;
    if (formData.industry !== undefined) profileData.industry = formData.industry;
    if (formData.position_level !== undefined) profileData.position_level = formData.position_level;
    if (formData.address !== undefined) profileData.address = formData.address;
    if (formData.birth_date !== undefined) profileData.birth_date = formData.birth_date;
    
    // Map gender values
    if (formData.gender !== undefined) {
      const genderMap = {
        '男性': 'male',
        '女性': 'female',
        'その他': 'other',
        '回答しない': 'prefer_not_to_say'
      };
      profileData.gender = genderMap[formData.gender] || formData.gender;
    }

    // Map language values
    if (formData.language !== undefined) {
      const languageMap = {
        '日本語': 'ja',
        '英語': 'en'
      };
      profileData.language = languageMap[formData.language] || formData.language;
    }

    return profileData;
  }
}